import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AnalyticsReporting.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const AnalyticsReporting = ({ onTabChange }) => {
  const { t } = useTranslation('dashboard');
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7d');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Global search filter function
  const shouldShowContent = (content, searchFields = []) => {
    if (!searchTerm.trim()) return true;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return searchFields.some(field => 
      field.toString().toLowerCase().includes(lowerSearchTerm)
    );
  };

  const tabs = [
    { id: 'overview', label: `📊 ${t('analytics.tabs.overview')}`, icon: 'dashboard' },
    { id: 'users-retention', label: `👤 ${t('analytics.tabs.usersRetention')}`, icon: 'people' },
    { id: 'courses-articles', label: `📚 ${t('analytics.tabs.coursesArticles')}`, icon: 'book' },
    { id: 'payments-revenue', label: `💰 ${t('analytics.tabs.paymentsRevenue')}`, icon: 'attach_money' },
    { id: 'teachers-schools', label: `🧑‍🏫 ${t('analytics.tabs.teachersSchools')}`, icon: 'school' },
    { id: 'custom-reports', label: `📈 ${t('analytics.tabs.customReports')}`, icon: 'assessment' }
  ];

  // Analytics data from backend
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalActiveUsers: { today: 0, week: 0, month: 0 },
      newRegistrations: { today: 0, week: 0, month: 0 },
      totalRevenue: { today: 0, week: 0, month: 0 },
      averageReadingTime: 0,
      topArticles: [],
      mostActiveSchools: []
    },
    usersRetention: {
      dailyActiveUsers: [0, 0, 0, 0, 0, 0, 0],
      weeklyActiveUsers: [0, 0, 0, 0, 0, 0, 0],
      churnRate: 0,
      averageSessionTime: 0,
      loginFrequency: 0,
      bounceRate: 0,
      retentionCohorts: {
        '7days': 0,
        '14days': 0,
        '30days': 0,
        '60days': 0,
        '90days': 0
      }
    },
    coursesArticles: {
      mostViewedArticles: [],
      averageReadTime: 0,
      clickRateVocabulary: 0,
      completionRates: {
        primary: 0,
        middle: 0,
        high: 0
      },
      timeSpentByLevel: {
        primary: 0,
        middle: 0,
        high: 0
      }
    },
    paymentsRevenue: {
      totalRevenue: { month: 0, year: 0 },
      revenueBreakdown: {
        online: 0,
        manual: 0
      },
      revenueByTier: {
        basic: 0,
        premium: 0,
        enterprise: 0
      },
      newSubscriptions: { week: 0, month: 0 },
      cancellations: { week: 0, month: 0 },
      arpu: 0,
      revenueBySchool: []
    },
    teachersSchools: {
      totalTeachers: 0,
      articlesAdded: 0,
      mostActiveTeachers: [],
      schoolActivityScores: []
    }
  });

  // Helper function to translate insights
  const translateInsight = (insight) => {
    // If insight has translation keys, use them
    if (insight.translationKey) {
      return {
        ...insight,
        title: t(`analytics.insights.titles.${insight.translationKey.title}`, { defaultValue: insight.title }),
        message: t(`analytics.insights.messages.${insight.translationKey.message}`, insight.messageParams || {}, { defaultValue: insight.message }),
        metric: insight.metric // Metrics are usually numbers/percentages, keep as is
      };
    }
    
    // Try to translate common patterns
    const translated = { ...insight };
    
    // Translate common titles
    if (insight.title) {
      const titleLower = insight.title.toLowerCase();
      if (titleLower.includes('user growth') || titleLower.includes('growth')) {
        translated.title = t('analytics.insights.titles.userGrowth', { defaultValue: insight.title });
      } else if (titleLower.includes('retention alert') || titleLower.includes('retention')) {
        translated.title = t('analytics.insights.titles.retentionAlert', { defaultValue: insight.title });
      } else if (titleLower.includes('engagement opportunity') || titleLower.includes('engagement')) {
        translated.title = t('analytics.insights.titles.engagementOpportunity', { defaultValue: insight.title });
      }
    }
    
    // Translate common message patterns
    if (insight.message) {
      // Pattern: "Users are reading X% more this week than last."
      const readingMatch = insight.message.match(/Users are reading (\d+)% more this week than last/i);
      if (readingMatch) {
        translated.message = t('analytics.insights.messages.readingMore', { percent: readingMatch[1] });
      }
      // Pattern: "You lost X Pro Plan users..."
      const lostUsersMatch = insight.message.match(/You lost (\d+) Pro Plan users/i);
      if (lostUsersMatch) {
        translated.message = t('analytics.insights.messages.lostProUsers', { count: lostUsersMatch[1] });
      }
      // Pattern: "X students clicked a word 3+ times but didn't save it"
      const clickedWordMatch = insight.message.match(/(\d+) students clicked a word 3\+ times but didn't save it/i);
      if (clickedWordMatch) {
        translated.message = t('analytics.insights.messages.clickedWordNotSaved', { count: clickedWordMatch[1] });
      }
    }
    
    return translated;
  };

  // Function to fetch analytics data from backend
  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/analytics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          // 'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const data = await response.json();
      setAnalyticsData(data);
      // Translate insights before setting them
      const translatedInsights = (data.insights || []).map(translateInsight);
      setInsights(translatedInsights);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(t('analytics.error'));
    } finally {
      setLoading(false);
    }
  };

  // Search filtering functions
  const filterInsights = (insights) => {
    if (!searchTerm.trim()) return insights;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return insights.filter(insight => 
      insight.title.toLowerCase().includes(lowerSearchTerm) ||
      insight.message.toLowerCase().includes(lowerSearchTerm) ||
      insight.metric.toLowerCase().includes(lowerSearchTerm)
    );
  };

  const filterArticles = (articles) => {
    if (!searchTerm.trim()) return articles;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return articles.filter(article => 
      article.title.toLowerCase().includes(lowerSearchTerm) ||
      article.views.toString().includes(searchTerm) ||
      article.engagement.toString().includes(searchTerm)
    );
  };

  const filterSchools = (schools) => {
    if (!searchTerm.trim()) return schools;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return schools.filter(school => 
      school.name.toLowerCase().includes(lowerSearchTerm) ||
      school.students.toString().includes(searchTerm) ||
      school.activity.toString().includes(searchTerm)
    );
  };

  const filterTeachers = (teachers) => {
    if (!searchTerm.trim()) return teachers;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return teachers.filter(teacher => 
      teacher.name.toLowerCase().includes(lowerSearchTerm) ||
      teacher.articles.toString().includes(searchTerm) ||
      teacher.students.toString().includes(searchTerm) ||
      teacher.engagement.toString().includes(searchTerm)
    );
  };

  const shouldShowMetric = (metricTitle, metricValue) => {
    if (!searchTerm.trim()) return true;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return metricTitle.toLowerCase().includes(lowerSearchTerm) ||
           metricValue.toString().toLowerCase().includes(lowerSearchTerm);
  };

  // Create searchable cards for metrics
  const createMetricCards = () => {
    const cards = [
      {
        title: t('analytics.metrics.totalActiveUsers'),
        value: analyticsData.overview?.totalActiveUsers?.today || 0,
        description: t('analytics.metrics.activeToday'),
        icon: 'people',
        onClick: () => {
          try {
            sessionStorage.setItem('overview_focus', 'active_users_today');
          } catch (_) {
            // ignore
          }
          if (typeof onTabChange === 'function') {
            onTabChange('overview');
          }
        }
      },
      {
        title: t('analytics.metrics.newRegistrations'),
        value: analyticsData.overview?.newRegistrations?.today || 0,
        description: t('analytics.metrics.newUsersToday'),
        icon: 'person_add'
      },
      {
        title: t('analytics.metrics.totalRevenue'),
        value: `$${analyticsData.overview?.totalRevenue?.today || 0}`,
        description: t('analytics.metrics.revenueToday'),
        icon: 'attach_money'
      },
      {
        title: t('analytics.metrics.averageReadingTime'),
        value: `${analyticsData.overview?.averageReadingTime || 0} min`,
        description: t('analytics.metrics.averageSession'),
        icon: 'timer'
      }
    ];

    if (!searchTerm.trim()) return cards;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return cards.filter(card => 
      card.title.toLowerCase().includes(lowerSearchTerm) ||
      card.description.toLowerCase().includes(lowerSearchTerm) ||
      card.value.toString().toLowerCase().includes(lowerSearchTerm)
    );
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, selectedCountry, selectedLanguage, selectedLevel]);

  const renderOverviewTab = () => (
    <div className="analytics-tab-content">
      {/* Loading and Error States */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('analytics.loading')}</p>
        </div>
      )}
      
      {error && (
        <div className="error-state">
          <span className="material-icons">error</span>
          <p>{error}</p>
          <button onClick={fetchAnalyticsData} className="retry-button">
            <span className="material-icons">refresh</span>
            {t('analytics.retry')}
          </button>
        </div>
      )}
      
      {!loading && !error && (
        <>
          <div className="analytics-header">
            <h3>📊 {t('analytics.overview')}</h3>
            <button onClick={fetchAnalyticsData} className="refresh-button" disabled={loading}>
              <span className="material-icons">refresh</span>
              {loading ? t('analytics.refreshing') : t('analytics.refreshData')}
            </button>
          </div>
          
          <div className="insights-section">
            <h3>🔍 {t('analytics.aiInsights')}</h3>
            <div className="insights-grid">
              {(() => {
                const filteredInsights = filterInsights(insights);
                if (filteredInsights.length > 0) {
                  return filteredInsights.map((insight, index) => {
                    const translatedInsight = translateInsight(insight);
                    return (
                      <div key={index} className={`insight-card ${insight.type}`}>
                        <div className="insight-header">
                          <span className="insight-icon">{insight.icon}</span>
                          <span className="insight-metric">{translatedInsight.metric}</span>
                        </div>
                        <h4>{translatedInsight.title}</h4>
                        <p>{translatedInsight.message}</p>
                      </div>
                    );
                  });
                } else if (searchTerm) {
                  return (
                    <div className="no-search-results">
                      <span className="material-icons">search_off</span>
                      <p>{t('analytics.noMatchInsights')} "{searchTerm}"</p>
                    </div>
                  );
                } else {
                  return (
                    <div className="no-insights">
                      <span className="material-icons">info</span>
                      <p>{t('analytics.noInsights')}</p>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </>
      )}

      <div className="metrics-grid">
        {(() => {
          const metricCards = createMetricCards();
          if (metricCards.length > 0) {
            return metricCards.map((card, index) => (
              <div
                key={index}
                className="metric-card"
                onClick={typeof card.onClick === 'function' ? card.onClick : undefined}
                style={{ cursor: typeof card.onClick === 'function' ? 'pointer' : 'default' }}
              >
                <div className="metric-header">
                  <span className="material-icons">{card.icon}</span>
                  <h4>{card.title}</h4>
                </div>
                <div className="metric-value-large">
                  {card.value}
                </div>
                <div className="metric-description">
                  {card.description}
                </div>
              </div>
            ));
          } else if (searchTerm.trim()) {
            return (
              <div className="no-search-results">
                <span className="material-icons">search_off</span>
                <p>{t('analytics.sections.noMatchMetrics')} "{searchTerm}"</p>
              </div>
            );
          } else {
            return (
              <div className="no-data">
                <span className="material-icons">analytics</span>
                <p>{t('analytics.sections.noMetrics')}</p>
              </div>
            );
          }
        })()}
      </div>

      <div className="charts-section">
        <div className="chart-container">
          <h4>{t('analytics.sections.topArticles')}</h4>
          <div className="articles-list">
            {(() => {
              const filteredArticles = filterArticles(analyticsData.overview.topArticles);
              if (filteredArticles.length > 0) {
                return filteredArticles.map((article, index) => (
                  <div key={index} className="article-item">
                    <span className="article-rank">#{index + 1}</span>
                    <span className="article-title">{article.title}</span>
                    <span className="article-views">{article.views.toLocaleString()} {t('analytics.metrics.views')}</span>
                    <span className="article-engagement">{article.engagement}% {t('analytics.metrics.engagement')}</span>
                  </div>
                ));
              } else if (searchTerm) {
                return (
                  <div className="no-search-results">
                    <span className="material-icons">search_off</span>
                    <p>{t('analytics.sections.noMatchArticles')} "{searchTerm}"</p>
                  </div>
                );
              } else {
                return (
                  <div className="no-data">
                    <span className="material-icons">article</span>
                    <p>{t('analytics.sections.noArticles')}</p>
                  </div>
                );
              }
            })()}
          </div>
        </div>

        <div className="chart-container">
          <h4>{t('analytics.sections.mostActiveSchools')}</h4>
          <div className="schools-list">
            {(() => {
              const filteredSchools = filterSchools(analyticsData.overview.mostActiveSchools);
              if (filteredSchools.length > 0) {
                return filteredSchools.map((school, index) => (
                  <div key={index} className="school-item">
                    <span className="school-rank">#{index + 1}</span>
                    <span className="school-name">{school.name}</span>
                    <span className="school-students">{school.students} {t('analytics.metrics.students')}</span>
                    <span className="school-activity">{school.activity}% {t('analytics.metrics.activity')}</span>
                  </div>
                ));
              } else if (searchTerm) {
                return (
                  <div className="no-search-results">
                    <span className="material-icons">search_off</span>
                    <p>{t('analytics.sections.noMatchSchools')} "{searchTerm}"</p>
                  </div>
                );
              } else {
                return (
                  <div className="no-data">
                    <span className="material-icons">school</span>
                    <p>{t('analytics.sections.noSchools')}</p>
                  </div>
                );
              }
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsersRetentionTab = () => (
    <div className="analytics-tab-content">
      <div className="metrics-grid">
        <div className="metric-card">
          <h4>{t('analytics.usersRetention.dailyActiveUsers')}</h4>
          <div className="chart-placeholder">
            <div className="chart-bars">
              {analyticsData.usersRetention.dailyActiveUsers.map((value, index) => (
                <div key={index} className="chart-bar" style={{ height: `${(value / 1500) * 100}%` }}>
                  <span className="bar-value">{value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>{t('analytics.usersRetention.retentionMetrics')}</h4>
          <div className="retention-metrics">
            <div className="retention-item">
              <span className="retention-label">{t('analytics.usersRetention.churnRate')}</span>
              <span className="retention-value">{analyticsData.usersRetention.churnRate}%</span>
            </div>
            <div className="retention-item">
              <span className="retention-label">{t('analytics.usersRetention.averageSessionTime')}</span>
              <span className="retention-value">{analyticsData.usersRetention.averageSessionTime} min</span>
            </div>
            <div className="retention-item">
              <span className="retention-label">{t('analytics.usersRetention.loginFrequency')}</span>
              <span className="retention-value">{analyticsData.usersRetention.loginFrequency} {t('analytics.usersRetention.timesPerWeek')}</span>
            </div>
            <div className="retention-item">
              <span className="retention-label">{t('analytics.usersRetention.bounceRate')}</span>
              <span className="retention-value">{analyticsData.usersRetention.bounceRate}%</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>{t('analytics.usersRetention.retentionCohorts')}</h4>
          <div className="cohorts-grid">
            {Object.entries(analyticsData.usersRetention.retentionCohorts).map(([period, rate]) => (
              <div key={period} className="cohort-item">
                <span className="cohort-period">{period}</span>
                <span className="cohort-rate">{rate}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCoursesArticlesTab = () => (
    <div className="analytics-tab-content">
      <div className="metrics-grid">
        <div className="metric-card">
          <h4>{t('analytics.coursesArticles.contentPerformance')}</h4>
          <div className="content-metrics">
            <div className="content-item">
              <span className="content-label">{t('analytics.coursesArticles.averageReadTime')}</span>
              <span className="content-value">{analyticsData.coursesArticles.averageReadTime} {t('analytics.coursesArticles.minutes')}</span>
            </div>
            <div className="content-item">
              <span className="content-label">{t('analytics.coursesArticles.vocabularyClickRate')}</span>
              <span className="content-value">{analyticsData.coursesArticles.clickRateVocabulary}%</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>{t('analytics.coursesArticles.completionRatesByLevel')}</h4>
          <div className="completion-rates">
            {Object.entries(analyticsData.coursesArticles.completionRates).map(([level, rate]) => (
              <div key={level} className="completion-item">
                <span className="completion-level">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                <div className="completion-bar">
                  <div className="completion-fill" style={{ width: `${rate}%` }}></div>
                </div>
                <span className="completion-rate">{rate}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-container">
        <h4>{t('analytics.coursesArticles.mostViewedArticles')}</h4>
        <div className="articles-performance">
          {analyticsData.coursesArticles.mostViewedArticles.map((article, index) => (
            <div key={index} className="article-performance-item">
              <span className="article-rank">#{index + 1}</span>
              <span className="article-title">{article.title}</span>
              <span className="article-views">{article.views.toLocaleString()} {t('analytics.metrics.views')}</span>
              <span className="article-read-time">{article.readTime} {t('analytics.coursesArticles.minRead')}</span>
              <span className="article-completion">{article.completion}% {t('analytics.coursesArticles.completion')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPaymentsRevenueTab = () => (
    <div className="analytics-tab-content">
      <div className="metrics-grid">
        <div className="metric-card">
          <h4>{t('analytics.paymentsRevenue.revenueOverview')}</h4>
          <div className="revenue-metrics">
            <div className="revenue-item">
              <span className="revenue-label">{t('analytics.paymentsRevenue.thisMonth')}</span>
              <span className="revenue-value">${analyticsData.paymentsRevenue.totalRevenue.month.toLocaleString()}</span>
            </div>
            <div className="revenue-item">
              <span className="revenue-label">{t('analytics.paymentsRevenue.thisYear')}</span>
              <span className="revenue-value">${analyticsData.paymentsRevenue.totalRevenue.year.toLocaleString()}</span>
            </div>
            <div className="revenue-item">
              <span className="revenue-label">{t('analytics.paymentsRevenue.arpu')}</span>
              <span className="revenue-value">${analyticsData.paymentsRevenue.arpu}</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>{t('analytics.paymentsRevenue.revenueBreakdown')}</h4>
          <div className="revenue-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-label">{t('analytics.paymentsRevenue.onlinePayments')}</span>
              <span className="breakdown-value">${analyticsData.paymentsRevenue.revenueBreakdown.online.toLocaleString()}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">{t('analytics.paymentsRevenue.manualPayments')}</span>
              <span className="breakdown-value">${analyticsData.paymentsRevenue.revenueBreakdown.manual.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>{t('analytics.paymentsRevenue.revenueByTier')}</h4>
          <div className="tier-revenue">
            {Object.entries(analyticsData.paymentsRevenue.revenueByTier).map(([tier, revenue]) => (
              <div key={tier} className="tier-item">
                <span className="tier-name">{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                <span className="tier-revenue">${revenue.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-container">
        <h4>{t('analytics.paymentsRevenue.revenueBySchool')}</h4>
        <div className="school-revenue">
          {analyticsData.paymentsRevenue.revenueBySchool.map((school, index) => (
            <div key={index} className="school-revenue-item">
              <span className="school-rank">#{index + 1}</span>
              <span className="school-name">{school.name}</span>
              <span className="school-revenue">${school.revenue.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTeachersSchoolsTab = () => (
    <div className="analytics-tab-content">
      <div className="metrics-grid">
        <div className="metric-card">
          <h4>{t('analytics.teachersSchools.teacherStatistics')}</h4>
          <div className="teacher-stats">
            <div className="stat-item">
              <span className="stat-label">{t('analytics.teachersSchools.totalTeachers')}</span>
              <span className="stat-value">{analyticsData.teachersSchools.totalTeachers}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('analytics.teachersSchools.articlesAdded')}</span>
              <span className="stat-value">{analyticsData.teachersSchools.articlesAdded}</span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h4>{t('analytics.teachersSchools.mostActiveTeachers')}</h4>
          <div className="active-teachers">
            {analyticsData.teachersSchools.mostActiveTeachers.map((teacher, index) => (
              <div key={index} className="teacher-item">
                <span className="teacher-rank">#{index + 1}</span>
                <span className="teacher-name">{teacher.name}</span>
                <span className="teacher-articles">{teacher.articles} {t('analytics.teachersSchools.articles')}</span>
                <span className="teacher-students">{teacher.students} {t('analytics.metrics.students')}</span>
                <span className="teacher-engagement">{teacher.engagement}% {t('analytics.metrics.engagement')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-container">
        <h4>{t('analytics.teachersSchools.schoolActivityScores')}</h4>
        <div className="school-activity">
          {analyticsData.teachersSchools.schoolActivityScores.map((school, index) => (
            <div key={index} className="school-activity-item">
              <span className="school-rank">#{index + 1}</span>
              <span className="school-name">{school.name}</span>
              <div className="school-score-bar">
                <div className="score-fill" style={{ width: `${school.score}%` }}></div>
              </div>
              <span className="school-score">{school.score}%</span>
              <span className="school-students">{school.students} {t('analytics.metrics.students')}</span>
              <span className="school-progress">{school.progress}% {t('analytics.teachersSchools.progress')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCustomReportsTab = () => (
    <div className="analytics-tab-content">
      <div className="reports-section">
        <h3>📊 {t('analytics.customReports.title')}</h3>
        
        <div className="report-filters">
          <div className="filter-group">
            <label>{t('analytics.customReports.reportType')}</label>
            <select className="filter-select">
              <option value="users">{t('analytics.customReports.userReport')}</option>
              <option value="teachers">{t('analytics.customReports.teacherReport')}</option>
              <option value="schools">{t('analytics.customReports.schoolReport')}</option>
              <option value="revenue">{t('analytics.customReports.revenueReport')}</option>
              <option value="content">{t('analytics.customReports.contentReport')}</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>{t('analytics.customReports.dateRange')}</label>
            <select className="filter-select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="7d">{t('analytics.filters.last7Days')}</option>
              <option value="30d">{t('analytics.filters.last30Days')}</option>
              <option value="90d">{t('analytics.filters.last90Days')}</option>
              <option value="1y">{t('analytics.filters.lastYear')}</option>
              <option value="custom">{t('analytics.customReports.customRange')}</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>{t('analytics.customReports.format')}</label>
            <select className="filter-select">
              <option value="csv">{t('analytics.customReports.csv')}</option>
              <option value="pdf">{t('analytics.customReports.pdf')}</option>
              <option value="excel">{t('analytics.customReports.excel')}</option>
            </select>
          </div>
        </div>

        <div className="report-actions">
          <button className="primary-btn">
            <span className="material-icons">download</span>
            {t('analytics.customReports.generateReport')}
          </button>
          
          <button className="secondary-btn">
            <span className="material-icons">schedule</span>
            {t('analytics.customReports.scheduleAutoExport')}
          </button>
        </div>

        <div className="scheduled-reports">
          <h4>{t('analytics.customReports.scheduledReports')}</h4>
          <div className="scheduled-list">
            <div className="scheduled-item">
              <span className="schedule-name">{t('analytics.customReports.weeklyRevenueReport')}</span>
              <span className="schedule-frequency">{t('analytics.customReports.everyMondayAt9AM')}</span>
              <span className="schedule-email">admin@nativelike.com</span>
              <button className="action-btn edit">{t('analytics.customReports.edit')}</button>
              <button className="action-btn delete">{t('analytics.customReports.delete')}</button>
            </div>
            <div className="scheduled-item">
              <span className="schedule-name">{t('analytics.customReports.monthlyUserAnalytics')}</span>
              <span className="schedule-frequency">{t('analytics.customReports.firstOfEveryMonth')}</span>
              <span className="schedule-email">admin@nativelike.com</span>
              <button className="action-btn edit">{t('analytics.customReports.edit')}</button>
              <button className="action-btn delete">{t('analytics.customReports.delete')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'users-retention':
        return renderUsersRetentionTab();
      case 'courses-articles':
        return renderCoursesArticlesTab();
      case 'payments-revenue':
        return renderPaymentsRevenueTab();
      case 'teachers-schools':
        return renderTeachersSchoolsTab();
      case 'custom-reports':
        return renderCustomReportsTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="section">
      <div className="analytics-header">
        <h2>{t('analytics.title')}</h2>
        <div className="analytics-filters">
          <select 
            className="filter-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7d">{t('analytics.filters.last7Days')}</option>
            <option value="30d">{t('analytics.filters.last30Days')}</option>
            <option value="90d">{t('analytics.filters.last90Days')}</option>
            <option value="1y">{t('analytics.filters.lastYear')}</option>
          </select>
          
          <select 
            className="filter-select"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            <option value="all">{t('analytics.filters.allCountries')}</option>
            <option value="algeria">Algeria</option>
            <option value="morocco">Morocco</option>
            <option value="tunisia">Tunisia</option>
          </select>
          
          <select 
            className="filter-select"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="all">{t('analytics.filters.allLanguages')}</option>
            <option value="english">English</option>
            <option value="french">French</option>
            <option value="arabic">Arabic</option>
          </select>
          
          <select 
            className="filter-select"
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
          >
            <option value="all">{t('analytics.filters.allLevels')}</option>
            <option value="primary">{t('analytics.filters.primary')}</option>
            <option value="middle">{t('analytics.filters.middle')}</option>
            <option value="high">{t('analytics.filters.high')}</option>
          </select>
        </div>
      </div>

      <div className="analytics-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`analytics-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.label.split(' ')[0]}</span>
            <span className="tab-label">{tab.label.split(' ').slice(1).join(' ')}</span>
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-container">
          <span className="material-icons search-icon">search</span>
          <input
            type="text"
            placeholder={t('analytics.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => {
              const newSearchTerm = e.target.value;
              setSearchTerm(newSearchTerm);
              console.log('🔍 Search triggered:', newSearchTerm);
              if (newSearchTerm.trim()) {
                console.log('📊 Search results:', {
                  insights: filterInsights(insights).length,
                  articles: filterArticles(analyticsData.overview?.topArticles || []).length,
                  schools: filterSchools(analyticsData.overview?.mostActiveSchools || []).length,
                  metrics: createMetricCards().length
                });
              }
            }}
            className="search-input"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')} 
              className="clear-search"
            >
              <span className="material-icons">clear</span>
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-info">
            🔍 {t('analytics.search.searchingFor')} "{searchTerm}" | 
            📊 {t('analytics.tabs.overview')}: {filterInsights(insights).length + filterArticles(analyticsData.overview?.topArticles || []).length + filterSchools(analyticsData.overview?.mostActiveSchools || []).length + createMetricCards().length} {t('analytics.search.results')}
            <br />
            <small>💡 {t('analytics.search.try')}: "growth", "user", "english", "excellence", "234", "revenue", "reading", "teacher", "school", "revenue"</small>
            <br />
            <small>🔧 {t('analytics.search.searchWorking')}</small>
          </div>
        )}
      </div>
      
      {renderTabContent()}
    </div>
  );
};

export default AnalyticsReporting; 